import _ from 'lodash';
import moment from 'moment-timezone';
import {
  DEFAULT_TEMPLATE,
  getInitialDate,
  SHOPPABLE_CATEGORY_ID,
  SHOPPABLE_TOPIC_NAME,
  clearEditableVideoFields,
} from './Template.model';
import {
  initTargeting,
  targetingFromAPI,
  targetingToAPI,
  trimTargeting,
} from './TemplateTargeting.utils';

export const CENTRAL_TZ = 'America/Chicago';

export function toCentralStartOfDay(date) {
  return moment.tz(date.format('YYYY-MM-DD'), CENTRAL_TZ).startOf('day');
}

export function toCentralEndOfDay(date) {
  return moment.tz(date.format('YYYY-MM-DD'), CENTRAL_TZ).endOf('day');
}

export function getDateValidationError(key, errorCode) {
  if (!errorCode) return null;

  switch (errorCode) {
    case 'invalidDate':
      return 'Please enter a valid date';
    case 'minDate':
      return key === 'endDate'
        ? 'End date should be after start date'
        : 'Please enter a valid date';
    default:
      return null;
  }
}

export function validateTemplate({
  template,
  startDate,
  endDate,
  evergreen,
  existingErrors = {},
}) {
  const newErrors = {};
  const requiredFields = ['category', 'message'];
  const isShoppableSelected = template.category === SHOPPABLE_CATEGORY_ID;

  if (isShoppableSelected) {
    if (!template.link) {
      newErrors.link = 'Product URL is required for Product Link templates';
    } else {
      try {
        // eslint-disable-next-line no-new
        new URL(template.link);
      } catch (error) {
        newErrors.link =
          'Please enter a valid Product URL (e.g., https://www.example.com)';
      }
    }
    if (!template.title) {
      newErrors.title = 'Product Name is required for Product Link templates';
    }
    if (!template.description) {
      newErrors.description =
        'Product Description is required for Product Link templates';
    }
  } else {
    requiredFields.push('topic', 'subtopic');
  }

  requiredFields.forEach((key) => {
    if (!template[key] && !newErrors[key]) {
      newErrors[key] = 'This field is required';
    }
  });

  if (
    template.category === 'fitb' &&
    template.message.search(/\{.+\}/g) === -1
  ) {
    newErrors.message =
      'Fill-in-the-Blank templates require at least one blank';
  }

  if (!startDate) {
    newErrors.startDate = 'This field is required';
  } else if (existingErrors.startDate) {
    newErrors.startDate = existingErrors.startDate;
  }

  if (!evergreen) {
    if (!endDate) {
      newErrors.endDate = 'This field is required';
    } else if (existingErrors.endDate) {
      newErrors.endDate = existingErrors.endDate;
    } else {
      const timeDiff = endDate.diff(startDate, 'hours');
      if (timeDiff <= 0) {
        newErrors.endDate = 'End date should be after start date';
      }
    }
  }

  return {
    errors: newErrors,
    isValid: _.isEmpty(newErrors),
  };
}

export function applyCategoryChange(currentTemplate, newCategory) {
  let updatedTemplate = {
    ...DEFAULT_TEMPLATE,
    ...currentTemplate,
    category: newCategory,
    link: newCategory === SHOPPABLE_CATEGORY_ID ? currentTemplate.link : '',
    topic: newCategory === SHOPPABLE_CATEGORY_ID ? '' : currentTemplate.topic,
    subtopic:
      newCategory === SHOPPABLE_CATEGORY_ID ? '' : currentTemplate.subtopic,
  };

  if (
    newCategory === SHOPPABLE_CATEGORY_ID &&
    currentTemplate.type === 'video'
  ) {
    updatedTemplate = clearEditableVideoFields(updatedTemplate);
  }

  return updatedTemplate;
}

export function getCategoryChangeErrors(prevErrors, newCategory) {
  if (newCategory === SHOPPABLE_CATEGORY_ID) {
    return {
      ...prevErrors,
      topic: undefined,
      subtopic: undefined,
      link: prevErrors.link,
      source: prevErrors.source,
    };
  }

  return {
    ...prevErrors,
    link: undefined,
    source: undefined,
    topic: prevErrors.topic,
    subtopic: prevErrors.subtopic,
  };
}

export function resolveShoppableTopicIds(topics) {
  const shoppableTopic = topics.find(
    (topic) => topic.name.toLowerCase() === SHOPPABLE_TOPIC_NAME
  );
  if (!shoppableTopic) {
    return {
      error:
        'Cannot create Product Link template: "Product Links" topic is missing.',
    };
  }

  const shoppableSubtopic = shoppableTopic.subtopics?.find(
    (subtopic) => subtopic.name.toLowerCase() === SHOPPABLE_TOPIC_NAME
  );
  if (!shoppableSubtopic) {
    return {
      error:
        'Cannot create Product Link template: "Product Links" subtopic is missing.',
    };
  }

  return {
    topicId: shoppableTopic.id,
    subtopicId: shoppableSubtopic.id || null,
  };
}

export function buildTemplatePayload({
  template,
  startDate,
  endDate,
  evergreen,
  targeting,
  topics,
}) {
  const payload = {
    ...template,
    cesdk_scene: template.cesdk_scene ?? null,
    cesdk_scene_metadata: template.cesdk_scene_metadata ?? null,
    start_date: startDate.format(),
    end_date: evergreen ? null : endDate.format(),
    targeting: targetingToAPI(trimTargeting(targeting)),
  };

  if (template.category !== SHOPPABLE_CATEGORY_ID) {
    return { payload };
  }

  const shoppableIds = resolveShoppableTopicIds(topics);
  if (shoppableIds.error) {
    return { error: shoppableIds.error };
  }

  return {
    payload: {
      ...payload,
      cta: 'SHOP_NOW',
      topic: shoppableIds.topicId,
      subtopic: shoppableIds.subtopicId,
      type: 'text',
    },
  };
}

export function hasTemplateChanges({
  template,
  initialTemplate,
  targeting,
  startDate,
  endDate,
  evergreen,
}) {
  const originalTemplate = { ...DEFAULT_TEMPLATE, ...initialTemplate };

  if (!_.isEqual(template, originalTemplate)) {
    return true;
  }

  const initialTargetingState = initialTemplate?.targeting
    ? targetingFromAPI(initialTemplate.targeting)
    : initTargeting();
  if (!_.isEqual(targeting, initialTargetingState)) {
    return true;
  }

  if (!initialTemplate) {
    return !!(startDate || endDate || evergreen);
  }

  const initialEvergreen = initialTemplate.end_date === null;
  if (evergreen !== initialEvergreen) {
    return true;
  }

  if (
    !!endDate !== !!initialTemplate.end_date ||
    (endDate && !endDate.isSame(initialTemplate.end_date, 'day')) ||
    !!startDate !== !!initialTemplate.start_date ||
    (startDate && !startDate.isSame(initialTemplate.start_date, 'day'))
  ) {
    return true;
  }

  return false;
}

export function shouldShowEndNowButton(initialTemplate, evergreen) {
  const currentEndDate = getInitialDate(initialTemplate?.end_date);
  return (
    Boolean(initialTemplate?.id) &&
    ((!currentEndDate && evergreen) ||
      (currentEndDate && currentEndDate.isAfter(moment())))
  );
}
